import { useState, useEffect } from "react"
import { supabase } from "@/integrations/supabase/client"
import { useAuth } from "./useAuth"
import { useProfiles } from "./useProfiles"
import { useAdmin } from "./useAdmin"

export interface EligibilityStatus {
  isEligible: boolean
  hasListings: boolean
  hasPaidBookings: boolean
  platformFeesCollected: number
  totalBookings: number
  isApprovedHost: boolean
  reason?: string
}

export const useCooperativeEligibility = () => {
  const { user } = useAuth()
  const { profile } = useProfiles()
  const { isAdmin } = useAdmin()
  const [eligibilityStatus, setEligibilityStatus] = useState<EligibilityStatus>({
    isEligible: false,
    hasListings: false,
    hasPaidBookings: false,
    platformFeesCollected: 0,
    totalBookings: 0,
    isApprovedHost: false
  })
  const [loading, setLoading] = useState(true)

  const checkEligibility = async () => {
    if (!user || !profile) {
      setLoading(false)
      return
    }

    try {
      // Admins bypass all eligibility requirements
      if (isAdmin()) {
        setEligibilityStatus({
          isEligible: true,
          hasListings: true,
          hasPaidBookings: true,
          platformFeesCollected: 100, // Show some collected fees for admins
          totalBookings: 5, // Show some bookings for admins
          isApprovedHost: true,
          reason: undefined
        })
        setLoading(false)
        return
      }

      // Check if user is approved host
      const isApprovedHost = profile.host_approved === true

      if (!isApprovedHost) {
        setEligibilityStatus({
          isEligible: false,
          hasListings: false,
          hasPaidBookings: false,
          platformFeesCollected: 0,
          totalBookings: 0,
          isApprovedHost: false,
          reason: "Nicht als Vermieter genehmigt"
        })
        setLoading(false)
        return
      }

      // Check if user has active listings
      const { data: listings, error: listingsError } = await supabase
        .from('listings')
        .select('id')
        .eq('user_id', user.id)
        .eq('is_active', true)

      if (listingsError) throw listingsError

      const hasListings = listings && listings.length > 0

      if (!hasListings) {
        setEligibilityStatus({
          isEligible: false,
          hasListings: false,
          hasPaidBookings: false,
          platformFeesCollected: 0,
          totalBookings: 0,
          isApprovedHost: true,
          reason: "Keine aktiven Unterkünfte vorhanden"
        })
        setLoading(false)
        return
      }

      // Check for paid bookings and platform fees
      const listingIds = listings.map(l => l.id)
      
      // Get completed bookings for user's listings
      const { data: bookings, error: bookingsError } = await supabase
        .from('bookings')
        .select('id, total_price, payment_status')
        .in('listing_id', listingIds)
        .eq('status', 'completed')
        .eq('payment_status', 'completed')

      if (bookingsError) throw bookingsError

      const hasPaidBookings = bookings && bookings.length > 0
      const totalBookings = bookings?.length || 0

      // Check platform fees collected for this host
      const { data: platformFees, error: feesError } = await supabase
        .from('platform_fees')
        .select('fee_amount, status')
        .eq('host_id', user.id)
        .eq('status', 'collected')

      if (feesError) throw feesError

      const platformFeesCollected = platformFees?.reduce((sum, fee) => sum + Number(fee.fee_amount), 0) || 0
      const hasCollectedFees = platformFeesCollected > 0

      const isEligible = isApprovedHost && hasListings && hasPaidBookings && hasCollectedFees

      let reason = ""
      if (!hasPaidBookings) {
        reason = "Keine abgeschlossenen bezahlten Buchungen"
      } else if (!hasCollectedFees) {
        reason = "Noch keine Plattformgebühren eingezogen"
      }

      setEligibilityStatus({
        isEligible,
        hasListings,
        hasPaidBookings,
        platformFeesCollected,
        totalBookings,
        isApprovedHost,
        reason: isEligible ? undefined : reason
      })

    } catch (error) {
      console.error('Error checking cooperative eligibility:', error)
      setEligibilityStatus({
        isEligible: false,
        hasListings: false,
        hasPaidBookings: false,
        platformFeesCollected: 0,
        totalBookings: 0,
        isApprovedHost: false,
        reason: "Fehler bei der Überprüfung"
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    checkEligibility()
  }, [user, profile])

  return {
    eligibilityStatus,
    loading,
    checkEligibility
  }
}